<template>
    <div>
        <div class="horizontal" style="height:80vh; width:90vw;">
            <div :id="'scichart-root-' + sid" style="width: 100%; margin-right: 10px;"></div>
        </div>
    </div>
</template>

<script>
import { SciChartSurface } from "scichart/Charting/Visuals/SciChartSurface";
import { NumericAxis } from "scichart/Charting/Visuals/Axis/NumericAxis";
import { EAxisAlignment } from "scichart/types/AxisAlignment";
import { FastLineRenderableSeries } from "scichart/Charting/Visuals/RenderableSeries/FastLineRenderableSeries";
import { XyDataSeries } from "scichart/Charting/Model/XyDataSeries";
import { ZoomPanModifier } from "scichart/Charting/ChartModifiers/ZoomPanModifier";
import { MouseWheelZoomModifier } from "scichart/Charting/ChartModifiers/MouseWheelZoomModifier";
import { RubberBandXyZoomModifier } from "scichart/Charting/ChartModifiers/RubberBandXyZoomModifier";
import { ZoomExtentsModifier } from "scichart/Charting/ChartModifiers/ZoomExtentsModifier";
import { RolloverModifier } from "scichart/Charting/ChartModifiers/RolloverModifier";
import { EllipsePointMarker } from "scichart/Charting/Visuals/PointMarkers/EllipsePointMarker";
import { SciChartJSLightTheme } from "scichart/Charting/Themes/SciChartJSLightTheme";
import { ELineDrawMode } from "scichart/Charting/Drawing/WebGlRenderContext2D"
import { NumberRange } from 'scichart/Core/NumberRange';
import { EXyDirection } from "scichart/types/XyDirection";
import { CursorModifier } from "scichart/Charting/ChartModifiers/CursorModifier";
import moment from 'moment'

export default {
    props: ['data', 'labelX', 'labelY', 'sid', 'zoom', 'pan', 'showValues', 'mouseZoom', 'initialZoomOut'],
    data() {
        return {
            sciChartSurface: null,
            wasmContext: null,
            xAxis: null,
            yAxis: null
        }
    },
    watch: {
        chartData: function(newVal, oldVal) {
            this.redraw();
        }
    },
    computed: {
        chartData() {
            return this.data;
        }
    },
    methods: {
        async initSciChart(){

            var xValues = this.data.xValues
            var yValues = this.data.yValues

            const {sciChartSurface, wasmContext} = await SciChartSurface.create("scichart-root-" + this.sid);

            this.sciChartSurface = sciChartSurface;
            this.wasmContext = wasmContext;

            await sciChartSurface.applyTheme(new SciChartJSLightTheme())

            let xAxis = new NumericAxis(this.wasmContext, { axisAlignment: EAxisAlignment.Top});
            let yAxis = new NumericAxis(this.wasmContext, { axisAlignment: EAxisAlignment.Left});
            this.xAxis = xAxis;
            this.yAxis = yAxis;

            if(this.initialZoomOut == true){
                yAxis.growBy = new NumberRange(0.1, 0.1);
                xAxis.growBy = new NumberRange(0.1, 0.1);
            }

            sciChartSurface.xAxes.add(this.xAxis)
            sciChartSurface.yAxes.add(this.yAxis)

            xAxis.labelProvider.formatLabel = (unixTimestamp) => {
                return moment(unixTimestamp).format("YYYY-MM-DD, HH:mm:ss")
            };

            var d = [];

            const lineSeries = new FastLineRenderableSeries(this.wasmContext, {
                stroke: "#0095B2",
                drawNaNAs: ELineDrawMode.DiscontinuousLine,
                strokeThickness: 2,
                pointMarker: new EllipsePointMarker(wasmContext, {
                    width: 6,
                    height: 6,
                    strokeThickness: 2,
                    fill: "#0095B230",
                    stroke: "#0095B2"
                })
            });

            if(!(xValues == undefined || xValues == null || xValues.length == 0)) {
                for(let i = 0; i < xValues.length; i++){
                    d[i] = new Date(moment(xValues[i])).getTime()
                }

                lineSeries.dataSeries = new XyDataSeries(this.wasmContext, {
                    xValues: d,
                    yValues: yValues
                });

                lineSeries.strokeThickness = 3;
                lineSeries.rolloverModifierProps.tooltipLabelX = this.labelX ? this.labelX : "X";
                lineSeries.rolloverModifierProps.tooltipLabelY = this.labelY ? this.labelY : "Y";

            } else {
                d = {}

                lineSeries.dataSeries = new XyDataSeries(this.wasmContext, {
                    xValues: {},
                    yValues: {}
                });
            }

            this.sciChartSurface.renderableSeries.add(lineSeries);

            if(this.zoomBySelection == true) sciChartSurface.chartModifiers.add(new RubberBandXyZoomModifier({ xyDirection: EXyDirection.XDirection }));
            if(this.pan != false ) sciChartSurface.chartModifiers.add(new ZoomPanModifier({ xyDirection: EXyDirection.XDirection }));
            if(this.zoom != false) sciChartSurface.chartModifiers.add(new ZoomExtentsModifier({ xyDirection: EXyDirection.XDirection }));
            if(this.mouseZoom != false) sciChartSurface.chartModifiers.add(new MouseWheelZoomModifier({ xyDirection: EXyDirection.XDirection }));

            const rolloverModifier = new RolloverModifier();
            rolloverModifier.showRolloverLine = false;
            this.sciChartSurface.chartModifiers.add(rolloverModifier);

            this.sciChartSurface.chartModifiers.add(new CursorModifier({
                crosshairStroke: "#0095B2",
                crosshairStrokeThickness: 1,
                tooltipContainerBackground: "#000",
                tooltipTextStroke: "#0095B2",
                showTooltip: false,
                axisLabelFill: "#0095B2",
                axisLabelStroke: "#fff",
            }))

            this.sciChartSurface.zoomExtents();
        },
        async redraw() {
            if(this.sciChartSurface == null) return;

            this.sciChartSurface.renderableSeries.items = [];

            if(this.data.length == 0){
                const dataSeries = new XyDataSeries(this.wasmContext, {
                    xValues: {},
                    yValues: {}
                })

                const lineSeries = new FastLineRenderableSeries(this.wasmContext, {
                    dataSeries
                })

                this.sciChartSurface.renderableSeries.add(lineSeries);
            }

            let xValues = this.data.xValues
            let yValues = this.data.yValues

            // Create an X,Y Axis and add to the chart
            this.xAxis = new NumericAxis(this.wasmContext, { axisAlignment: EAxisAlignment.Top});
            this.yAxis = new NumericAxis(this.wasmContext, { axisAlignment: EAxisAlignment.Left});

            this.xAxis.labelProvider.formatLabel = (unixTimestamp) => {
                return moment(unixTimestamp).format("DD MMM, HH:mm")
            };

            var d = [];
            for(let i = 0; i < xValues.length; i++){
                d[i] = new Date(moment(xValues[i])).getTime()
            }

            const lineSeries = new FastLineRenderableSeries(this.wasmContext, {
                stroke: "#0095B2",
                drawNaNAs: ELineDrawMode.DiscontinuousLine,
                strokeThickness: 2,
                pointMarker: new EllipsePointMarker(this.wasmContext, {
                    width: 6,
                    height: 6,
                    strokeThickness: 2,
                    fill: "#0095B230",
                    stroke: "#0095B2"
                })
            });

            // Declare a DataSeries
            lineSeries.dataSeries = new XyDataSeries(this.wasmContext, {
                xValues: d,
                yValues: yValues
            });

            lineSeries.strokeThickness = 3;
            lineSeries.rolloverModifierProps.tooltipLabelX = this.labelX ? this.labelX : "X";
            lineSeries.rolloverModifierProps.tooltipLabelY = this.labelY ? this.labelY : "Y";
            this.sciChartSurface.renderableSeries.add(lineSeries);

            this.sciChartSurface.zoomExtents();
        },
        applyStyles(){
            this.sciChartSurface.applyTheme({
                annotationsGripsBackroundBrush: "white",
                annotationsGripsBorderBrush: "white",
                axis3DBandsFill: "#1F3D6833",
                axisBandsFill: "#1F3D6833",
                axisPlaneBackgroundFill: "Transparent",
                columnFillBrush: "white",
                columnLineColor: "white",
                cursorLineBrush: "#6495ED99",
                defaultColorMapBrush: [
                    { offset: 0, color: "DarkBlue" },
                    { offset: 0.5, color: "CornflowerBlue" },
                    { offset: 1, color: "#FF22AA" }
                ],
                downBandSeriesFillColor: "#52CC5490",
                downBandSeriesLineColor: "#E26565FF",
                downBodyBrush: "white",
                downWickColor: "white",
                gridBackgroundBrush: "white",
                gridBorderBrush: "white",
                labelBackgroundBrush: "#6495EDAA",
                labelBorderBrush: "#6495ED",
                labelForegroundBrush: "#EEEEEE",
                legendBackgroundBrush: "#1D2C35",
                lineSeriesColor: "#0095B2",
                majorGridLineBrush: "#1F3D68",
                minorGridLineBrush: "#102A47",
                mountainAreaBrush: "white",
                mountainLineColor: "white",
                overviewFillBrush: "white",
                planeBorderColor: "white",
                rolloverLineBrush: "#FD9F2533",
                rubberBandFillBrush: "#99999933",
                rubberBandStrokeBrush: "#99999977",
                sciChartBackground: "#0D213A",
                scrollbarBackgroundBrush: "white",
                scrollbarBorderBrush: "white",
                scrollbarGripsBackgroundBrush: "white",
                scrollbarViewportBackgroundBrush: "white",
                scrollbarViewportBorderBrush: "white",
                shadowEffectColor: "white",
                textAnnotationBackground: "#6495EDAA",
                textAnnotationForeground: "#EEEEEE",
                tickTextBrush: "#6495ED",
                upBandSeriesFillColor: "white",
                upBandSeriesLineColor: "white",
                upBodyBrush: "#6495EDA0",
                upWickColor: "#6495ED"
            });
        }
    },
    mounted: async function() {
        this.initSciChart();
    },
    beforeDestroy: async function() {
        if(this.sciChartSurface || this.wasmContext){
            this.sciChartSurface = null
            this.wasmContext = null
        }
        this.sciChartSurface = null
        this.wasmContext = null
    }
}
</script>



// WEBPACK FOOTER //
// src/components/SciChart.vue